import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Cigarette, 
  Brain, 
  Smile, 
  Frown, 
  ThermometerSun,
  Coffee,
  Moon,
  Pill,
  LucideIcon 
} from 'lucide-react';

interface LogCardGridWidgetProps {
  config: Record<string, unknown>;
  data?: unknown;
  onLog?: (logType: string) => Promise<void>;
}

interface LogCardConfig {
  id: string;
  label: string;
  icon: string;
  logType: string;
  color?: string;
}

const ICON_MAP: Record<string, LucideIcon> = {
  cigarette: Cigarette,
  brain: Brain,
  smile: Smile,
  frown: Frown,
  thermometer: ThermometerSun,
  coffee: Coffee,
  moon: Moon,
  pill: Pill,
};

const DEFAULT_LOG_CARDS: LogCardConfig[] = [
  { id: 'cigarette', label: 'Cigarette', icon: 'cigarette', logType: 'cigarette', color: 'bg-orange-500/10 text-orange-600' },
  { id: 'mood', label: 'Mood', icon: 'smile', logType: 'mood', color: 'bg-yellow-500/10 text-yellow-600' },
  { id: 'craving', label: 'Craving', icon: 'brain', logType: 'craving', color: 'bg-red-500/10 text-red-600' },
  { id: 'stress', label: 'Stress', icon: 'thermometer', logType: 'stress', color: 'bg-purple-500/10 text-purple-600' },
];

export function LogCardGridWidget({ config, onLog }: LogCardGridWidgetProps) {
  const title = (config.title as string) || 'Quick Log';
  const cards = (config.cards as LogCardConfig[]) || DEFAULT_LOG_CARDS;
  const columns = (config.columns as number) || 2;
  const variant = (config.variant as 'compact' | 'expanded') || 'compact';

  const handleLog = async (logType: string) => {
    if (onLog) {
      await onLog(logType);
    }
  };

  if (variant === 'expanded') {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {cards.map((card) => {
              const Icon = ICON_MAP[card.icon] || Brain;
              return (
                <Button
                  key={card.id}
                  variant="outline"
                  className={`h-auto py-4 flex-col gap-2 ${card.color || ''}`}
                  onClick={() => handleLog(card.logType)}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-sm font-medium">{card.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact variant - inline grid
  return (
    <Card className="h-full">
      <CardContent className="p-4">
        <div 
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))` }}
        >
          {cards.map((card) => {
            const Icon = ICON_MAP[card.icon] || Brain;
            return (
              <Button
                key={card.id}
                variant="ghost"
                size="sm"
                className={`h-auto py-2 flex-col gap-1 ${card.color || ''}`}
                onClick={() => handleLog(card.logType)}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs">{card.label}</span>
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
